import type { AppliedFilters } from '@/components/marketplace/BreedingValueFilters';

export const calculateDistance = (
  lat1: number, lon1: number, lat2: number, lon2: number
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

interface FilterOptions {
  searchTerm: string;
  auctionMatchesSearch: boolean;
  selectedBreed: string;
  selectedType: string;
  selectedLocation: string;
  locationCoordinates: [number, number] | null;
  rangeKm: number;
  locationType: string | null;
  appliedAdvancedFilters?: AppliedFilters;
}

export const filterListings = (listings: any[], options: FilterOptions) => {
  const {
    searchTerm, auctionMatchesSearch, selectedBreed, selectedType,
    selectedLocation, locationCoordinates, rangeKm, locationType, appliedAdvancedFilters,
  } = options;

  return listings.filter((listing: any) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!listing.title.toLowerCase().includes(term) && !auctionMatchesSearch) return false;
    }
    if (selectedBreed && listing.breed !== selectedBreed) return false;
    if (selectedType !== 'all') {
      const listingType = (listing.type || '').toLowerCase();
      if (!listingType.includes(selectedType.toLowerCase())) return false;
    }
    if (selectedLocation) {
      const isBroadArea = locationType === 'country' || locationType === 'region';
      if (isBroadArea) {
        const locationParts = selectedLocation.split(',').map(p => p.trim().toLowerCase());
        const loc = (listing.location || '').toLowerCase();
        if (!locationParts.some(part => loc.includes(part))) return false;
      } else if (locationCoordinates) {
        const [searchLng, searchLat] = locationCoordinates;
        if (!listing.location_coordinates) return false;
        try {
          let lng: number, lat: number;
          if (typeof listing.location_coordinates === 'string') {
            const c = JSON.parse(listing.location_coordinates);
            lng = c.longitude; lat = c.latitude;
          } else {
            const c = listing.location_coordinates as any;
            if (c.longitude && c.latitude) { lng = c.longitude; lat = c.latitude; }
            else if (Array.isArray(c)) { [lng, lat] = c; }
            else if (c.coordinates && Array.isArray(c.coordinates)) { [lng, lat] = c.coordinates; }
            else return false;
          }
          if (calculateDistance(searchLat, searchLng, lat, lng) > rangeKm) return false;
        } catch { return false; }
      }
    }
    if (appliedAdvancedFilters) {
      const pMin = appliedAdvancedFilters.priceMin ? parseFloat(appliedAdvancedFilters.priceMin) : NaN;
      const pMax = appliedAdvancedFilters.priceMax ? parseFloat(appliedAdvancedFilters.priceMax) : NaN;
      if (!isNaN(pMin) && (listing.price === null || listing.price < pMin)) return false;
      if (!isNaN(pMax) && (listing.price === null || listing.price > pMax)) return false;
      const activeBVFilters = Object.entries(appliedAdvancedFilters.breedingValues || {}).filter(
        ([, f]) => f.value !== ''
      );
      if (activeBVFilters.length > 0) {
        for (const [key, filter] of activeBVFilters) {
          const listingVal = (listing as any)[key];
          if (listingVal === null || listingVal === undefined || listingVal === '') continue;
          const lv = parseFloat(String(listingVal));
          const fv = parseFloat(filter.value);
          const variance = filter.variance ? parseFloat(filter.variance) : 0;
          if (isNaN(lv) || isNaN(fv)) return false;
          if (lv < fv - variance || lv > fv + variance) return false;
        }
      }
    }
    return true;
  });
};

export const transformListings = (filteredListings: any[]) => {
  return filteredListings.map((listing: any) => {
    const profile = listing.profiles;
    let imageUrl = 'https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=400&h=300&fit=crop';
    if (listing.images && Array.isArray(listing.images) && listing.images.length > 0) {
      imageUrl = String(listing.images[0]);
    }
    const evaluationData: Record<string, string> = {};
    if (listing.calving_ease) evaluationData.calvingEase = listing.calving_ease;
    if (listing.birth_weight) evaluationData.birthWeight = listing.birth_weight;
    if (listing.weaning_weight) evaluationData.weaningWeight = listing.weaning_weight;
    if (listing.milk) evaluationData.milk = listing.milk;

    return {
      id: listing.id,
      title: listing.title,
      image: imageUrl,
      price: listing.price
        ? `${listing.price_currency || '$'} ${Number(listing.price).toLocaleString()}`
        : undefined,
      location: listing.location || 'Location not specified',
      breeder:
        profile?.business_name ||
        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
        'Unknown Breeder',
      breed: listing.breed,
      listing_type: listing.listing_type,
      ...(Object.keys(evaluationData).length > 0 ? { evaluationData } : {}),
    };
  });
};
